import { act, devOpen, modalOpen, net, offer, pauseMenu } from "./store.js";

/**
 * Centralized, *gated* keyboard handler for camera + dev/self-test toggles and
 * the level-up hotkeys. Movement (WASD) is owned by `input.ts` (already gated).
 *
 * Gating rules (fixes the reported bug where `l`, `` ` ``, etc. fired while typing):
 *  - If focus is in an editable element → ignore everything.
 *  - Level-up 1/2/3 → only while an offer is open.
 *  - Camera/dev keys → ignored while typing; otherwise active. `` ` `` toggles the Dev panel.
 */

const PITCH_STEP = 2;
const DIST_STEP = 1.5;
const FOV_STEP = 2;

function isTypingTarget(t: EventTarget | null): boolean {
  const el = t instanceof HTMLElement ? t : null;
  if (el === null) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
}

export function installKeys(target: Window): () => void {
  const onKey = (e: KeyboardEvent): void => {
    if (isTypingTarget(e.target)) return; // never steal keystrokes from a field
    const a = act();

    // Esc: toggle the in-run pause menu (Resume / Restart / Lobby). Closes if open;
    // otherwise opens only during an active run (not in the lobby/end screens, which
    // have their own menus). Ignored while a level-up offer is up.
    if (e.code === "Escape") {
      if (offer.value !== null) return;
      const phase = net.value?.runPhase;
      if (pauseMenu.value) {
        e.preventDefault();
        pauseMenu.value = false;
      } else if (phase === "running" || phase === "boss") {
        e.preventDefault();
        pauseMenu.value = true;
      }
      return;
    }

    // Level-up hotkeys only while the overlay is open.
    if (offer.value !== null) {
      const idx = e.code === "Digit1" ? 0 : e.code === "Digit2" ? 1 : e.code === "Digit3" ? 2 : -1;
      if (idx >= 0) {
        e.preventDefault();
        a?.choose(offer.value.choices[idx]?.id ?? -1);
        offer.value = null;
        return;
      }
    }

    if (e.code === "Backquote") {
      e.preventDefault();
      devOpen.value = !devOpen.value;
      return;
    }

    if (a === null) return;
    switch (e.code) {
      case "Space":
        // Toggle auto ↔ manual aim (B4). Gated: not while typing/in a menu.
        if (!e.repeat && !modalOpen()) a.toggleAim();
        break;
      case "KeyC":
        if (!e.repeat) a.cameraToggle();
        break;
      case "KeyL":
        if (!e.repeat && !modalOpen()) a.cycleLatency();
        break;
      case "KeyP":
        if (!e.repeat && !modalOpen()) a.togglePrediction();
        break;
      case "KeyR":
        if (!e.repeat && !modalOpen()) a.toggleRewind();
        break;
      case "BracketLeft":
        a.cameraTune({ pitchDeltaDeg: -PITCH_STEP });
        break;
      case "BracketRight":
        a.cameraTune({ pitchDeltaDeg: PITCH_STEP });
        break;
      case "Minus":
        a.cameraTune({ distanceDelta: DIST_STEP });
        break;
      case "Equal":
        a.cameraTune({ distanceDelta: -DIST_STEP });
        break;
      case "Semicolon":
        a.cameraTune({ fovDelta: -FOV_STEP });
        break;
      case "Quote":
        a.cameraTune({ fovDelta: FOV_STEP });
        break;
      case "Backslash":
        a.cameraTune({ logState: true });
        break;
      default:
        return;
    }
    e.preventDefault();
  };

  target.addEventListener("keydown", onKey);
  return () => target.removeEventListener("keydown", onKey);
}
